import React, { useState, useEffect } from 'react';
import { siteService } from '../../api/siteService';
import '../Common/Common.css';

/**
 * Create / edit a vitrine site. Only DATA the storefront consumes lives here:
 * identity, domain, marketing pixels and contact info. The visual design is a
 * per-site theme in the storefront codebase, keyed by `key`.
 */
function SiteForm({ site, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    key: '',
    name: '',
    domain: '',
    is_active: true,
    facebook_pixel_id: '',
    tiktok_pixel_id: '',
    google_analytics_id: '',
    contact_phone: '',
    contact_email: '',
    contact_whatsapp: '',
    contact_address: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (site) {
      setFormData({
        key: site.key || '',
        name: site.name || '',
        domain: site.domain || '',
        is_active: site.is_active ?? true,
        facebook_pixel_id: site.facebook_pixel_id || '',
        tiktok_pixel_id: site.tiktok_pixel_id || '',
        google_analytics_id: site.google_analytics_id || '',
        contact_phone: site.contact_phone || '',
        contact_email: site.contact_email || '',
        contact_whatsapp: site.contact_whatsapp || '',
        contact_address: site.contact_address || '',
      });
    }
  }, [site]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    let v = type === 'checkbox' ? checked : value;
    // Keep the key url-safe as the user types.
    if (name === 'key') v = v.toLowerCase().replace(/[^a-z0-9-]/g, '');
    setFormData((prev) => ({ ...prev, [name]: v }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!formData.key || !formData.name) {
      setError('La clé et le nom sont obligatoires.');
      return;
    }
    setLoading(true);
    try {
      if (site) {
        await siteService.updateSite(site.id, formData);
      } else {
        await siteService.createSite(formData);
      }
      onSuccess();
    } catch (err) {
      setError(err.response?.data?.message || 'Échec de l\'enregistrement du site');
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="form">
      {error && (
        <div style={{ marginBottom: 12, padding: '10px 12px', background: '#fff5f5', color: '#c53030', border: '1px solid #feb2b2', borderRadius: 6, fontSize: 14 }}>
          {error}
        </div>
      )}

      <div className="form-group">
        <label>Nom du site *</label>
        <input type="text" name="name" value={formData.name} onChange={handleChange}
               className="form-control" placeholder="Carlinkit Maroc" required />
      </div>

      <div className="form-group">
        <label>Clé (identifiant technique) *</label>
        <input type="text" name="key" value={formData.key} onChange={handleChange}
               className="form-control" placeholder="carlinkit"
               disabled={!!site} required />
        <small>
          Minuscules, chiffres et tirets. Utilisée par le storefront (VITE_SITE_KEY) et
          comme origine des commandes. {site && 'Non modifiable après création.'}
        </small>
      </div>

      <div className="form-group">
        <label>Nom de domaine</label>
        <input type="text" name="domain" value={formData.domain} onChange={handleChange}
               className="form-control" placeholder="carlinkit.ma" />
      </div>

      <div className="form-group">
        <label className="checkbox-label">
          <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleChange} />
          <span>Site actif</span>
        </label>
        <small>Un site inactif ne sert plus de produits ni de commandes.</small>
      </div>

      <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid #edf2f7' }} />
      <h4 style={{ margin: '0 0 12px', color: '#2d3748' }}>Pixels marketing</h4>

      <div className="form-group">
        <label>Facebook Pixel ID</label>
        <input type="text" name="facebook_pixel_id" value={formData.facebook_pixel_id}
               onChange={handleChange} className="form-control" placeholder="1234567890" />
      </div>
      <div className="form-group">
        <label>TikTok Pixel ID</label>
        <input type="text" name="tiktok_pixel_id" value={formData.tiktok_pixel_id}
               onChange={handleChange} className="form-control" />
      </div>
      <div className="form-group">
        <label>Google Analytics ID</label>
        <input type="text" name="google_analytics_id" value={formData.google_analytics_id}
               onChange={handleChange} className="form-control" placeholder="G-XXXXXXXXXX" />
      </div>

      <hr style={{ margin: '18px 0', border: 0, borderTop: '1px solid #edf2f7' }} />
      <h4 style={{ margin: '0 0 12px', color: '#2d3748' }}>Coordonnées</h4>

      <div className="form-group">
        <label>Téléphone</label>
        <input type="text" name="contact_phone" value={formData.contact_phone}
               onChange={handleChange} className="form-control" />
      </div>
      <div className="form-group">
        <label>WhatsApp</label>
        <input type="text" name="contact_whatsapp" value={formData.contact_whatsapp}
               onChange={handleChange} className="form-control" />
      </div>
      <div className="form-group">
        <label>Email</label>
        <input type="email" name="contact_email" value={formData.contact_email}
               onChange={handleChange} className="form-control" />
      </div>
      <div className="form-group">
        <label>Adresse</label>
        <input type="text" name="contact_address" value={formData.contact_address}
               onChange={handleChange} className="form-control" />
      </div>

      <div className="form-actions">
        <button type="button" onClick={onCancel} className="btn btn-secondary">Annuler</button>
        <button type="submit" disabled={loading} className="btn btn-primary">
          {loading ? 'Enregistrement…' : site ? 'Modifier' : 'Créer'}
        </button>
      </div>
    </form>
  );
}

export default SiteForm;
